import React, { useEffect, useState } from 'react';
import {
  ArrowLeftIcon,
  BookOpenIcon,
  CpuChipIcon,
  DocumentTextIcon,
  PauseIcon,
  PlayIcon,
  PuzzlePieceIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { useNavigate, useParams } from 'react-router-dom';
import OverviewTab from '../components/AgentConfig/tabs/OverviewTab';
import AISettingsTab from '../components/AgentConfig/tabs/AISettingsTab';
import ActionsTab from '../components/AgentConfig/tabs/ActionsTab';
import KnowledgeBaseTab from '../components/AgentConfig/tabs/KnowledgeBaseTab';
import QandATab from '../components/AgentConfig/tabs/QandATab';
import { agentInstanceService } from '../services/agentService';
import IntegrationsTab from '../components/AgentConfig/tabs/IntegrationsTab';
import DatabaseTab from '../components/AgentConfig/tabs/DatabaseTab';

const mockAgent = {
  id: 'agent-001',
  name: 'Customer Support Agent',
  description: 'AI-powered customer support agent for handling inquiries and support requests',
  type: 'customer-support',
  status: 'active',
  model: 'GPT-4',
  version: '1.2.3',
  createdAt: '2024-01-15',
  lastUpdated: '2024-01-20',
  deletedAt: '2024-01-20',
  totalInteractions: 1250,
  successRate: 94.5,
  avgResponseTime: 2.3,
  icon: '💬',
  color: 'from-blue-500 to-blue-600',
  capabilities: [
    'Natural Language Processing',
    'Intent Recognition',
    'Multi-language Support',
    'Context Awareness',
    'Escalation Management'
  ],
  integrations: [
    { name: 'Zendesk', status: 'connected', type: 'ticket-system' },
    { name: 'Slack', status: 'connected', type: 'messaging' },
    { name: 'Salesforce', status: 'disconnected', type: 'crm' }
  ],
  knowledgeBase: {
    totalDocuments: 45,
    lastUpdated: '2024-01-18',
    categories: ['FAQ', 'Product Info', 'Troubleshooting', 'Policies']
  },

  agentType: {
    code: "",
    id: "",
    name: ""
  }
};

function mapApiToUi(data: any) {
  if (!data) return {
    id: '',
    name: 'Untitled Agent',
    description: '',
    type: 'unknown',
    status: 'inactive',
    model: '',
    version: '',
    createdAt: '',
    lastUpdated: '',
    totalInteractions: 0,
    successRate: 0,
    avgResponseTime: 0,
    icon: '🤖',
    color: 'from-gray-200 to-gray-300',
    capabilities: [],
    integrations: [],
    knowledgeBase: { totalDocuments: 0, lastUpdated: '', categories: [] },
    agentType: null,
    department: null,
    deletedAt: null,
    code: '',
    state: '',
    metadata: {},
    aiSettings: null
  };

  const rawType = (data.agentType?.name ?? data.agentType?.code ?? '').toString();
  const uiType = rawType ? rawType.toLowerCase().replace(/\s+/g, '-') : 'unknown';
  const aiSettings = data.aiSettings ?? null;
  const ai = aiSettings?.settings ?? {};

  return {
    id: data.id ?? '',
    name: data.name ?? data.code ?? 'Untitled Agent',
    description: data.description ?? '',
    type: uiType,
    status: typeof data.isActive === 'boolean' ? (data.isActive ? 'active' : 'inactive') : (data.status ?? 'inactive'),
    model: data.model ?? ai.model ?? '',
    version: data.version ? String(data.version) : (String(aiSettings?.schemaVersion ?? '') || (data.versionDescription ?? '')),
    createdAt: data.createdAt ?? data.created_at ?? '',
    lastUpdated: data.updatedAt ?? data.lastUpdated ?? '',
    totalInteractions: data.metrics?.totalInteractions ?? 0,
    successRate: data.metrics?.successRate ?? 0,
    avgResponseTime: data.metrics?.avgResponseTime ?? 0,
    icon: data.icon ?? '🤖',
    color: data.color ?? 'from-gray-200 to-gray-300',
    capabilities: data.capabilities ?? data.capabilityList ?? [],
    integrations: data.integrations ?? [],
    knowledgeBase: data.knowledgeBase ?? { totalDocuments: 0, lastUpdated: '', categories: [] },
    agentType: data.agentType ?? null,
    department: data.department ?? null,
    code: data.code ?? '',
    state: data.state ?? '',
    metadata: data.metadata ?? {},
    deletedAt: data.deletedAt,
    aiSettings
  };
}


interface TabConfig {
  id: string;
  name: string;
  icon: React.ComponentType<any>;
  component: React.ComponentType<any>;
}

const AgentConfig: React.FC = () => {
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [agent, setAgent] = useState(mockAgent);
  const [isLoading, setIsLoading] = useState(false);
  const [agentTypeId, setAgentTypeId] = useState<string>("");


  const [error, setError] = useState<string | null>(null);


  useEffect(() => {
    if (!agentId) return;

    let mounted = true;
    setIsLoading(true);
    setError(null);

    const fetchAgent = async () => {
      try {
        const resp = await agentInstanceService.getAgentInstanceById(encodeURIComponent(agentId), { includeDeleted: true });
        const payload = (resp && resp.data) ? resp.data : resp;

        if (mounted) setAgentTypeId(payload?.agentType?.id);

        const mapped = mapApiToUi(payload);

        if (mounted) setAgent(mapped);
      } catch (err: any) {
        console.error('Failed to fetch agent instance', err);
        if (mounted) setError(err?.message ?? 'Failed to load agent');
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    fetchAgent();

    return () => { mounted = false; };
  }, [agentId, activeTab]);


  // Tab configuration
  const tabs: TabConfig[] = [
    {
      id: 'overview',
      name: 'Overview',
      icon: DocumentTextIcon,
      component: ({ agent }) => <OverviewTab agent={agent} />
    },
    {
      id: 'actions',
      name: 'Actions',
      icon: PlayIcon,
      component: ({ agent }) => <ActionsTab agent={agent} />
    },
    {
      id: 'ai-settings',
      name: 'AI Settings',
      icon: CpuChipIcon,
      component: ({ agent }) => <AISettingsTab agent={agent} />
    },
    {
      id: 'knowledge-base',
      name: 'Knowledge Base',
      icon: BookOpenIcon,
      component: ({ agent }) => <KnowledgeBaseTab agent={agent} />
    },
    {
      id: 'q-and-a',
      name: 'Q & A',
      icon: BookOpenIcon,
      component: ({ agent }) => <QandATab agent={agent} />
    },
    {
      id: 'integrations',
      name: 'Integrations',
      icon: PuzzlePieceIcon,
      component: ({ agent }) => <IntegrationsTab agent={agent} />
    },

    // {
    //   id: 'conversation-audit',
    //   name: 'Conversation Audit',
    //   icon: ChatBubbleLeftRightIcon,
    //   component: ConversationAuditTab
    // },


    // {
    //   id: 'logs-analytics',
    //   name: 'Logs & Analytics',
    //   icon: ChartBarIcon,
    //   component: ({ agent }) => <LogsAnalyticsTab agent={agent} />
    // }
  ];

  if (agent?.agentType?.code === "TENANT_PLATFORM_DB_FAQ") {
    tabs.push({
      id: 'database',
      name: 'Database',
      icon: CpuChipIcon,
      component: ({ agent }) => <DatabaseTab agent={agent} />
    })
  }

  const activeTabConfig = tabs.find(tab => tab.id === activeTab);
  const ActiveComponent = activeTabConfig?.component;

  const handleStatusToggle = () => {
    setAgent(prev => ({
      ...prev,
      status: prev.status === 'active' ? 'inactive' : 'active'
    }));
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this agent? This action cannot be undone.')) {
      navigate('/agents');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-agentic-white-off via-agentic-primary-blue/5 to-agentic-accent-purple/10">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-white/30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/agents')}
                className="p-2 text-agentic-text-secondary hover:text-agentic-text-primary transition-colors"
              >
                <ArrowLeftIcon className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-agentic-text-primary via-agentic-primary-blue to-agentic-accent-purple bg-clip-text text-transparent">
                  {agent.name}
                </h1>
                <p className="mt-1 text-agentic-text-secondary">{agent.description}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {/* <button
                onClick={handleStatusToggle}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${agent.status === 'active'
                  ? 'bg-agentic-accent-green text-white hover:bg-agentic-accent-green/90'
                  : 'bg-agentic-gray-300 text-agentic-text-primary hover:bg-agentic-gray-400'
                  }`}
              >
                {agent.status === 'active' ? (
                  <>
                    <PauseIcon className="h-4 w-4 inline mr-2" />
                    Pause
                  </>
                ) : (
                  <>
                    <PlayIcon className="h-4 w-4 inline mr-2" />
                    Activate
                  </>
                )}
              </button> */}
              {
                agent.deletedAt ? <></> :
                  <button
                    onClick={handleDelete}
                    className="px-4 py-2 bg-agentic-accent-red text-white rounded-lg font-medium hover:bg-agentic-accent-red/90 transition-colors"
                  >
                    <TrashIcon className="h-4 w-4 inline mr-2" />
                    Delete
                  </button>
              }
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tab Navigation */}
        <div className="bg-white rounded-xl border shadow-sm mb-6">
          <div className="border-b border-agentic-gray-200">
            <nav className="flex space-x-8 px-6" aria-label="Tabs">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === tab.id
                      ? 'border-agentic-primary-blue text-agentic-primary-blue'
                      : 'border-transparent text-agentic-text-secondary hover:text-agentic-text-primary hover:border-agentic-gray-300'
                      }`}
                  >
                    <Icon className="h-5 w-5 inline mr-2" />
                    {tab.name}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-xl border shadow-sm">
          {ActiveComponent && <ActiveComponent key={agent.id || 'agent'} agent={agent} />}
        </div>

      </div>
    </div>
  );
};

export default AgentConfig;
